/**
 * Interface para o modelo Status (StatusDTO)
 * Baseado na documentação da API de Gerenciamento de Status
 */
export interface Status {
  id?: number;
  code?: string;                    // Código único (gerado automaticamente: ENTITYTYPE_NAME)
  name: string;
  description?: string;
  entityType: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  isDeleted?: boolean;
}

/**
 * Interface para criar um novo Status
 * IMPORTANTE: O campo 'code' é gerado automaticamente pelo backend
 * Formato: ENTITYTYPE_NAME em UPPERCASE
 * Exemplo: EntityType="Customer", Name="Ativo" → "CUSTOMER_ATIVO"
 */
export interface StatusCreate {
  name: string;                     // Nome do status (obrigatório)
  description?: string;
  entityType: string;               // Tipo da entidade (obrigatório)
  isActive?: boolean;
}

/**
 * Interface para atualizar um Status
 * IMPORTANTE: O campo 'code' é regenerado automaticamente quando name ou entityType são alterados
 */
export interface StatusUpdate {
  name: string;                     // Obrigatório
  entityType: string;               // Obrigatório
  description?: string;
}

/**
 * Interface para resposta da API
 */
export interface StatusApiResponse {
  success: boolean;
  message?: string;
  data?: Status;
}

/**
 * Interface para resposta paginada da API
 */
export interface StatusPagedResponse {
  success: boolean;
  data: Status[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  filter?: {
    entityType?: string;
  };
}

/**
 * Interface para resposta de lista da API
 */
export interface StatusListResponse {
  success: boolean;
  data: Status[];
  count: number;
  entityType?: string;
  activeOnly?: boolean;
}

/**
 * Interface para verificação de código
 */
export interface StatusCodeCheckResponse {
  success: boolean;
  code: string;
  exists: boolean;
}

/**
 * Tipos de entidades disponíveis no sistema
 * Baseado nos models e módulos reais do ERP
 */
export type EntityType = 
  | 'Customer'
  | 'Vehicle'
  | 'VehicleRental'
  | 'Contract'
  | 'Staff'
  | 'Department'
  | 'Position'
  | 'Supplier'
  | 'PurchaseOrder'
  | 'AccountsPayable'
  | 'AccountsReceivable'
  | 'CostCenter'
  | 'Stock'
  | 'Item'
  | 'StockMovement'
  | 'Warehouse'
  | 'ServiceOrder'
  | 'ServiceType'
  | 'Product'
  | 'PreventiveMaintenance'
  | 'ExternalServiceProvider'
  | 'BankStatement'
  | 'BankTransaction'
  | 'Checklist'
  | 'B2BContract'
  | string;

/**
 * Lista de tipos de entidades com labels em português
 * Organizada por módulo do sistema
 */
export const ENTITY_TYPES: { value: EntityType; label: string; description: string; module: string }[] = [
  // Comercial
  { value: 'Customer', label: 'Cliente', description: 'Cadastro de clientes', module: 'Comercial' },
  { value: 'VehicleRental', label: 'Locação', description: 'Locações de veículos', module: 'Comercial' },
  { value: 'Contract', label: 'Contrato', description: 'Contratos comerciais', module: 'Comercial' },
  
  // Recursos Humanos
  { value: 'Staff', label: 'Funcionário', description: 'Colaboradores e funcionários', module: 'RH' },
  { value: 'Department', label: 'Departamento', description: 'Departamentos da empresa', module: 'RH' },
  { value: 'Position', label: 'Cargo', description: 'Cargos e funções', module: 'RH' },
  
  // Veículos
  { value: 'Vehicle', label: 'Veículo', description: 'Veículos da frota', module: 'Veículos' },
  
  // Compras
  { value: 'Supplier', label: 'Fornecedor', description: 'Fornecedores', module: 'Compras' },
  { value: 'PurchaseOrder', label: 'Pedido de Compra', description: 'Ordens de compra', module: 'Compras' },
  
  // Financeiro
  { value: 'AccountsPayable', label: 'Contas a Pagar', description: 'Contas a pagar', module: 'Financeiro' },
  { value: 'AccountsReceivable', label: 'Contas a Receber', description: 'Contas a receber', module: 'Financeiro' },
  { value: 'CostCenter', label: 'Centro de Custo', description: 'Centros de custo', module: 'Controladoria' },
  { value: 'BankStatement', label: 'Extrato Bancário', description: 'Extratos bancários', module: 'Financeiro' },
  { value: 'BankTransaction', label: 'Transação Bancária', description: 'Transações bancárias', module: 'Financeiro' },
  
  // Estoque
  { value: 'Stock', label: 'Estoque', description: 'Controle de estoque', module: 'Estoque' },
  { value: 'Item', label: 'Item', description: 'Itens de estoque', module: 'Estoque' },
  { value: 'StockMovement', label: 'Movimentação de Estoque', description: 'Movimentações de estoque', module: 'Estoque' },
  { value: 'Warehouse', label: 'Almoxarifado', description: 'Almoxarifados e depósitos', module: 'Estoque' },
  
  // Produtos
  { value: 'Product', label: 'Produto', description: 'Produtos comercializados', module: 'Produtos' },
  
  // Operações
  { value: 'ServiceOrder', label: 'Ordem de Serviço', description: 'Ordens de serviço', module: 'Operações' },
  { value: 'ServiceType', label: 'Tipo de Serviço', description: 'Tipos de serviços prestados', module: 'Operações' },
  { value: 'PreventiveMaintenance', label: 'Manutenção Preventiva', description: 'Manutenções preventivas', module: 'Operações' },
  
  // B2B
  { value: 'ExternalServiceProvider', label: 'Prestador Externo', description: 'Prestadores de serviço externos', module: 'B2B' },
  { value: 'B2BContract', label: 'B2B Contratos', description: 'Contratos B2B', module: 'B2B' },
  
  // Outros
  { value: 'Checklist', label: 'Checklist', description: 'Checklists e formulários', module: 'Outros' }
];

